'use client';

import * as React from 'react';
import { Input, type InputProps } from '@/shared/components/ui/input';
import {
  createCardInputHandlers,
  createCardInputRef,
} from '@/shared/lib/bankCardInput.ts';

const CARD_MASK = '0000 0000 0000 0000';

interface CardInputProps extends Omit<InputProps, 'leftContent'> {
  cardIconConfig?: Record<string, string>; // e.g., {4: 'icon_url', '5': 'icon_url'}
}

export function detectCardIcon(
  cardNumber: string,
  cardIconConfig?: Record<string, string>
): string | null {
  if (!cardNumber || !cardIconConfig) {
    return null;
  }

  const cleanCardNumber = cardNumber.replace(/\s/g, '');
  if (!cleanCardNumber) {
    return null;
  }

  const sortedKeys = Object.keys(cardIconConfig).sort(
    (a, b) => b.length - a.length
  );

  for (const key of sortedKeys) {
    if (cleanCardNumber.startsWith(key)) {
      return cardIconConfig[key] || null;
    }
  }

  return null;
}

const BankCardInput = React.forwardRef<HTMLInputElement, CardInputProps>(
  ({ value, onChange, cardIconConfig, className, ...props }, ref) => {
    const [inputValue, setInputValue] = React.useState(value || '');
    const [isFocused, setIsFocused] = React.useState(false);
    const [maskLeftPadding, setMaskLeftPadding] = React.useState(0);
    const inputRef = React.useRef<HTMLInputElement>(null);
    const shouldRestoreFocusRef = React.useRef(false);

    React.useEffect(() => {
      setInputValue(value || '');
    }, [value]);

    React.useEffect(() => {
      const checkFocus = () => {
        if (inputRef.current) {
          setIsFocused(document.activeElement === inputRef.current);
        }
      };

      checkFocus();

      const handleFocusIn = () => {
        setTimeout(checkFocus, 0);
      };

      const handleFocusOut = () => {
        setTimeout(checkFocus, 0);
      };

      document.addEventListener('focusin', handleFocusIn);
      document.addEventListener('focusout', handleFocusOut);

      return () => {
        document.removeEventListener('focusin', handleFocusIn);
        document.removeEventListener('focusout', handleFocusOut);
      };
    }, []);

    const handleValueChange = React.useCallback(
      (newValue: string) => {
        // Normalize to digits only for actual user input / storage.
        // Explanation: masked display uses `inputValue` directly; for user input we strip non-digits.
        const digitsOnly = newValue.replace(/\D/g, '');
        setInputValue(digitsOnly);
        const syntheticEvent = {
          target: { value: digitsOnly },
          currentTarget: { value: digitsOnly },
        } as React.ChangeEvent<HTMLInputElement>;
        onChange?.(syntheticEvent);
      },
      [onChange]
    );

    const cardHandlers = React.useMemo(
      () => createCardInputHandlers(handleValueChange),
      [handleValueChange]
    );

    const maskRef = React.useMemo(() => createCardInputRef(), []);

    const isMaskedValue = React.useMemo(() => {
      // Important: derive from the *prop* value so we can remount immediately
      // when RHF sets masked card numbers after initial render.
      return typeof value === 'string' && value.includes('*');
    }, [value]);

    const handleRef = React.useCallback(
      (node: HTMLInputElement | null) => {
        // Allow `*` to display only for initial masked values.
        // Once user interacts (clears), we re-enable the digit-only mask.
        if (node && !isMaskedValue) {
          maskRef(node);
        }
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref && node) {
          (ref as React.MutableRefObject<HTMLInputElement>).current = node;
        }
        if (node) {
          inputRef.current = node;
        }
      },
      [ref, maskRef, isMaskedValue]
    );

    const handleFocus = React.useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        props.onFocus?.(e);
      },
      [props]
    );

    const handleBlur = React.useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        props.onBlur?.(e);
      },
      [props]
    );

    const detectedIcon = React.useMemo(() => {
      return detectCardIcon(inputValue as string, cardIconConfig);
    }, [inputValue, cardIconConfig]);

    const shouldShowLeftContent =
      isFocused || (inputValue as string).length > 0;

    const formattedValue = React.useMemo(() => {
      if (isMaskedValue) {
        // Explanation: render the masked string as-is (including '*').
        return value as string;
      }
      if (!inputValue) return '';
      const digits = String(inputValue).replace(/\D/g, '');
      const chunks = [];
      for (let i = 0; i < digits.length; i += 4) {
        chunks.push(digits.slice(i, i + 4));
      }
      return chunks.join(' ');
    }, [inputValue, isMaskedValue, value]);

    const showMaskPlaceholder = isFocused && !formattedValue;

    const leftContent = React.useMemo(() => {
      if (!shouldShowLeftContent) return null;

      return (
        <div className="size-[2.4rem] mr-[0.8rem] flex items-center justify-center mt-[1rem]">
          {detectedIcon ? (
            <img
              src={detectedIcon}
              alt="Card"
              className="w-[2.4rem] h-[2.4rem] object-contain"
            />
          ) : (
            <div className="w-[1.8rem] h-[1.2rem] bg-tertiary rounded-[0.3rem]"></div>
          )}
        </div>
      );
    }, [shouldShowLeftContent, detectedIcon]);

    const containerRef = React.useRef<HTMLDivElement>(null);
    const calculateMaskPosition = React.useCallback(() => {
      if (inputRef.current && containerRef.current && showMaskPlaceholder) {
        requestAnimationFrame(() => {
          if (inputRef.current && containerRef.current) {
            const input = inputRef.current;
            const container = containerRef.current;
            const inputRect = input.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();
            const computedStyle = window.getComputedStyle(input);
            const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;

            const leftOffset =
              inputRect.left - containerRect.left + paddingLeft;
            setMaskLeftPadding(leftOffset);
          }
        });
      }
    }, [showMaskPlaceholder]);

    React.useEffect(() => {
      calculateMaskPosition();
    }, [calculateMaskPosition, leftContent, props.rightContent]);

    React.useEffect(() => {
      if (isFocused && showMaskPlaceholder) {
        calculateMaskPosition();
      }
    }, [isFocused, showMaskPlaceholder, calculateMaskPosition]);

    React.useEffect(() => {
      if (shouldRestoreFocusRef.current && inputRef.current) {
        inputRef.current.focus();
        shouldRestoreFocusRef.current = false;
      }
    }, [isMaskedValue, formattedValue]);

    const handleChange = React.useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.value.includes('*')) {
          // If masked content is being edited, clear the field completely.
          shouldRestoreFocusRef.current = true;
          handleValueChange('');
          return;
        }
        cardHandlers.change(event);
      },
      [cardHandlers, handleValueChange]
    );

    const handlePaste = React.useCallback(
      (event: React.ClipboardEvent<HTMLInputElement>) => {
        if (event.currentTarget.value.includes('*')) {
          // Clear the DOM value too, so paste insertion doesn't keep '*'.
          event.currentTarget.value = '';
          shouldRestoreFocusRef.current = true;
          handleValueChange('');
        }
        cardHandlers.paste(event);
      },
      [cardHandlers, handleValueChange]
    );

    return (
      <div ref={containerRef} className="relative w-full">
        <Input
          key={isMaskedValue ? 'bank-card-masked' : 'bank-card-normal'}
          {...props}
          ref={handleRef}
          value={formattedValue}
          onChange={handleChange}
          onPaste={handlePaste}
          onFocus={handleFocus}
          onBlur={handleBlur}
          leftContent={leftContent}
          className={className}
        />

        {showMaskPlaceholder && (
          <div
            className="absolute top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground/50 z-0 p3 pt-[0.8rem]"
            style={{ left: `${maskLeftPadding}px` }}
            aria-hidden="true">
            {CARD_MASK}
          </div>
        )}
      </div>
    );
  }
);

BankCardInput.displayName = 'BankCardInput';

export { BankCardInput };
